import { computeAssembly } from "@/lib/assembly";
import { profileCode } from "@/lib/catalogue";
import { subtypeOf } from "@/lib/iwin";
import { iwinAccessories } from "@/lib/hardware";
import { typologyLabel } from "@/lib/customTypologies";
import { infillOf, INFILLS } from "@/lib/infill";

export const DEFAULT_BOM = { bar: 5000, kerf: 4, trim: 20, mode: "item", gst: 18 };
const num = (v, d, min = 0) => (v === "" || v == null || Number.isNaN(+v) || +v < min ? d : +v);
export function bomSettings(tb) {
  const b = tb?.bom || {}, D = DEFAULT_BOM;
  return { bar: num(b.bar, D.bar, 500), kerf: num(b.kerf, D.kerf), trim: num(b.trim, D.trim), mode: b.mode === "project" ? "project" : "item", gst: num(b.gst, D.gst) };
}
const qtyOf = (it) => it.quantity || 1;
const sysOf = (it) => it.profile_system || "YKK AP";
const r1 = (v) => Math.round(v * 10) / 10, r2 = (v) => Math.round(v * 100) / 100;
const addTag = (map, tag, n) => map.set(tag, (map.get(tag) || 0) + n);
const tagList = (map, unit = "") => [...map].map(([t, n]) => `${t} ×${unit === "m" ? r1(n) : n}`).join(", ");

export function quotationRows(items) {
  return items.map((it, i) => {
    const area = (it.width * it.height) / 1e6, q = qtyOf(it);
    return { sr: i + 1, tag: it.tag, desc: it.name || it.type, typology: typologyLabel(it.typology),
      system: sysOf(it), w: it.width, h: it.height, area: r2(area), qty: q, total: r2(area * q), glass: it.glass_spec || "—", location: it.location || "" };
  });
}

export function pieces(it) {
  const q = qtyOf(it);
  return computeAssembly(it).members.flatMap((m) => {
    const code = profileCode(it, m.name);
    return Array.from({ length: m.qty * q }, () => ({ key: `${sysOf(it)}||${m.name}`, name: m.name, code, system: sysOf(it), len: m.len, mark: m.mark, tag: it.tag }));
  });
}

export function profileBom(items) {
  const rows = new Map();
  items.flatMap(pieces).forEach((p) => {
    const r = rows.get(p.key) || { key: p.key, name: p.name, code: p.code, system: p.system, count: 0, mm: 0, tags: new Map() };
    r.count++; r.mm += p.len; r.code = r.code || p.code; addTag(r.tags, p.tag, 1);
    rows.set(p.key, r);
  });
  return [...rows.values()].map((r) => ({ ...r, m: r2(r.mm / 1000), tags: tagList(r.tags) }));
}

function specBom(items, keep) {
  const rows = new Map();
  items.forEach((it) => computeAssembly(it).glass.forEach((g) => {
    if (!keep(g.spec)) return;
    const r = rows.get(g.spec) || { spec: g.spec, panes: 0, area: 0, tags: new Map() };
    const n = g.qty * qtyOf(it);
    r.panes += n; r.area += (g.w * g.h * n) / 1e6; addTag(r.tags, it.tag, n);
    rows.set(g.spec, r);
  }));
  return [...rows.values()].map((r) => ({ ...r, area: r2(r.area), tags: tagList(r.tags) }));
}

export const glassBom = (items) => specBom(items, (spec) => !infillOf(spec));

export function infillBom(items) {
  const rows = specBom(items, (spec) => !!infillOf(spec)).map((r) => ({ ...r, mat: infillOf(r.spec) }))
    .sort((a, b) => INFILLS.indexOf(a.mat) - INFILLS.indexOf(b.mat) || a.spec.localeCompare(b.spec));
  const totals = INFILLS.map((mat) => {
    const rs = rows.filter((r) => r.mat === mat);
    return rs.length ? { mat, panes: rs.reduce((a, r) => a + r.panes, 0), area: r2(rs.reduce((a, r) => a + r.area, 0)) } : null;
  }).filter(Boolean);
  return { rows, totals };
}

const genericHardware = (it) => computeAssembly(it).accessories.map(([label, v]) => ({ key: label, name: label, spec: "", qty: parseFloat(v) || 0, unit: /\sm$/.test(v) ? "m" : "nos" }));

export function hardwareOrder(items) {
  const groups = new Map();
  items.forEach((it) => {
    const s = subtypeOf(it), gk = s ? `IWIN-S · ${s.name}` : `Other systems · ${sysOf(it)}`, q = qtyOf(it);
    const g = groups.get(gk) || { group: gk, iwin: !!s, rows: new Map() };
    (s ? iwinAccessories(it) : genericHardware(it)).forEach((h) => {
      if (!h.qty) return;
      const r = g.rows.get(h.key) || { key: h.key, name: h.name, spec: h.spec, unit: h.unit, qty: 0, tags: new Map() };
      r.qty += h.qty * q; addTag(r.tags, it.tag, h.qty * q);
      g.rows.set(h.key, r);
    });
    groups.set(gk, g);
  });
  return [...groups.values()].sort((a, b) => b.iwin - a.iwin).map((g) => ({
    group: g.group, rows: [...g.rows.values()].map((r) => ({ ...r, qty: r.unit === "m" ? r1(r.qty) : r.qty, tags: tagList(r.tags, r.unit) })),
  }));
}

function packBars(list, { bar, kerf, trim }) {
  const usable = bar - 2 * trim, bars = [], oversize = [];
  [...list].sort((a, b) => b.len - a.len).forEach((p) => {
    if (p.len > usable) { oversize.push(p); return; }
    let best = null;
    bars.forEach((b) => { const free = usable - b.used; if (p.len <= free && (!best || free < usable - best.used)) best = b; });
    if (!best) { best = { cuts: [], used: 0 }; bars.push(best); }
    best.cuts.push(p);
    best.used += p.len + kerf;
  });
  const out = bars.map((b) => ({ cuts: b.cuts, used: b.cuts.reduce((a, c) => a + c.len, 0), offcut: Math.max(0, Math.round(usable - b.used + kerf)) }));
  const patterns = [];
  out.forEach((b) => {
    const sig = b.cuts.map((c) => `${c.len}${c.mark}${c.tag}`).join("|");
    const p = patterns.find((x) => x.sig === sig);
    if (p) p.n++; else patterns.push({ ...b, sig, n: 1 });
  });
  return { bars: out.length, patterns, oversize };
}

export function cutPlan(items, settings) {
  const groups = new Map();
  items.forEach((it) => pieces(it).forEach((p) => {
    const k = settings.mode === "project" ? p.key : `${p.tag}||${p.key}`;
    const g = groups.get(k) || { key: k, tag: settings.mode === "project" ? null : p.tag, name: p.name, code: p.code, system: p.system, list: [] };
    g.list.push(p); g.code = g.code || p.code;
    groups.set(k, g);
  }));
  const res = [...groups.values()].map(({ list, ...g }) => {
    const pk = packBars(list, settings), cut = list.reduce((a, p) => a + p.len, 0), stock = pk.bars * settings.bar;
    return { ...g, ...pk, pieces: list.length, cut, stock, waste: stock ? r1((100 * (stock - cut)) / stock) : 0 };
  });
  const stock = res.reduce((a, g) => a + g.stock, 0), cut = res.reduce((a, g) => a + g.cut, 0);
  return { groups: res, bars: res.reduce((a, g) => a + g.bars, 0), stock, cut, waste: stock ? r1((100 * (stock - cut)) / stock) : 0, oversize: res.flatMap((g) => g.oversize) };
}

export function barsByProfile(plan) {
  const m = new Map();
  plan.groups.forEach((g) => { const k = `${g.system}||${g.name}`; m.set(k, (m.get(k) || 0) + g.bars); });
  return m;
}

export const cutLabel = (c) => `${c.len} ${c.mark}·${c.tag}`;
